import { ExpandableBehaviorType, ExpandableRow } from './types';

export interface ExpandableBehavior {
  expanded(row: ExpandableRow): boolean;
  expand(row: ExpandableRow): ExpandableBehavior;
  retract(row: ExpandableRow): ExpandableBehavior;
  row(): ExpandableRow;
}

export class ExpandOne implements ExpandableBehavior {
  constructor(private _row: ExpandableRow, private _manualClose: boolean) {}

  public expanded(row: ExpandableRow): boolean {
    if (this._row === 'all') {
      return true;
    }

    return row === this._row;
  }

  public expand(row: ExpandableRow): ExpandableBehavior {
    if (this.expanded(row) && !this._manualClose) {
      return this.retract(row);
    }

    return new ExpandOne(row, this._manualClose);
  }

  public retract(row: ExpandableRow): ExpandableBehavior {
    return new ExpandOne('none', this._manualClose);
  }

  public row(): ExpandableRow {
    return this._row;
  }
}

export class ExpandEach implements ExpandableBehavior {
  private readonly _all: boolean;
  private readonly _none: boolean;
  private readonly _rows: number[];
  private readonly _manualClose: boolean;

  constructor(row: ExpandableRow, manualClose: boolean) {
    this._all = row === 'all';
    this._none = row === 'none';
    this._rows = Array.isArray(row) ? row : [row as number];
    this._manualClose = manualClose;
  }

  public expanded(row: ExpandableRow): boolean {
    if (this._all || this._none) {
      return this._all || !this._none;
    }

    if (typeof row === 'number') {
      return this._rows.includes(row);
    }

    return false;
  }

  public expand(row: ExpandableRow): ExpandableBehavior {
    if (this.expanded(row) && !this._manualClose) {
      return this.retract(row);
    }

    const rows = Array.isArray(row) ? row : [row as number];
    return new ExpandEach(
      [...new Set([...this._rows, ...rows])],
      this._manualClose
    );
  }

  public retract(row: ExpandableRow): ExpandableBehavior {
    const rows = this._rows.filter((r) => r !== row);
    return new ExpandEach(rows, this._manualClose);
  }

  public row(): ExpandableRow {
    if (this._all) {
      return 'all';
    }

    if (this._none) {
      return 'none';
    }

    return this._rows;
  }
}

export class ExpandableState implements ExpandableBehavior {
  constructor(
    private readonly _behavior: ExpandableBehavior,
    private readonly _manualClose = false
  ) {}

  static init(
    type: ExpandableBehaviorType = 'one',
    manualClose: boolean = false
  ): ExpandableBehavior {
    const behavior = ExpandableState._instatiate(type, manualClose);
    return new ExpandableState(behavior, manualClose);
  }

  private static _instatiate(
    type: ExpandableBehaviorType = 'one',
    manualClose: boolean = false
  ): ExpandableBehavior {
    switch (type) {
      case 'one':
        return new ExpandOne('none', manualClose);
      case 'each':
        return new ExpandEach('none', manualClose);
    }
  }

  public expanded(row: ExpandableRow): boolean {
    return this._behavior.expanded(row);
  }

  public expand(row: ExpandableRow): ExpandableBehavior {
    return this._behavior.expand(row);
  }

  public retract(row: ExpandableRow): ExpandableBehavior {
    return this._behavior.retract(row);
  }

  public row(): ExpandableRow {
    return this._behavior.row();
  }
}
